/**
 * Browser Position Defense Bot
 * Automatically adds missing TP/SL orders to open positions
 * 
 * Features:
 * - Polls positions every 1 minute
 * - Matches positions by asset AND exact size
 * - Two sequential checks before adding missing TP/SL
 * - Handles longs and shorts of same asset with different sizes
 * - Uses configurable TP% and SL% from localStorage
 * - Classifies TP vs SL based on side and price relative to entry
 */
class BrowserPositionDefenseBot {
    constructor() {
        this.isEnabled = false;
        this.isInitialized = false;
        
        // Cross-tab coordination
        this.mode = 'inactive'; // 'leader', 'follower', 'inactive'
        
        // Settings (loaded from localStorage)
        this.settings = this.getDefaultSettings();
        
        // Polling interval
        this.pollInterval = null;
        this.pollIntervalMs = 30000; // 30 seconds
        
        // Two-poll verification tracking
        // Map<key, { asset, size, side, missingTP, missingSL, timestamp }>
        this.pendingVerification = new Map();
        
        // console.log('[PositionDefenseBot] 🛡️ Initializing Position Defense Bot...');
        
        // Load settings and initialize
        this.loadSettings();
        this.initialize();
        
        // console.log(`[PositionDefenseBot] Loaded state - Enabled: ${this.isEnabled}, TP%: ${this.settings.tpPercent}, SL%: ${this.settings.slPercent}`);
    }

    /**
     * Get default settings
     */
    getDefaultSettings() {
        return {
            enabled: false,
            tpPercent: 2.0,
            slPercent: 2.0
        };
    }

    /**
     * Initialize the bot
     */
    async initialize() {
        try {
            this.isInitialized = true;
            // console.log('[PositionDefenseBot] ✅ Bot initialized successfully');
            
            // Start polling if enabled
            if (this.isEnabled) {
                this.startPolling();
            }
            
            return true;
        } catch (error) {
            // console.error('[PositionDefenseBot] ❌ Initialization error:', error);
            return false;
        }
    }

    /**
     * Load settings from localStorage
     */
    loadSettings() {
        try {
            const saved = localStorage.getItem('positionDefenseBotSettings');
            if (saved) {
                const loadedSettings = JSON.parse(saved);
                this.settings = { ...this.getDefaultSettings(), ...loadedSettings };
                this.isEnabled = this.settings.enabled || false;
                // console.log('[PositionDefenseBot] Settings loaded from localStorage');
            } else {
                this.saveSettings();
                // console.log('[PositionDefenseBot] Created default settings');
            }
        } catch (error) {
            // console.error('[PositionDefenseBot] Error loading settings:', error);
            this.settings = this.getDefaultSettings();
        }
    }

    /**
     * Save settings to localStorage
     */
    saveSettings() {
        try {
            localStorage.setItem('positionDefenseBotSettings', JSON.stringify(this.settings));
            // console.log('[PositionDefenseBot] Settings saved to localStorage');
        } catch (error) {
            // console.error('[PositionDefenseBot] Error saving settings:', error);
        }
    }

    /**
     * Toggle bot enabled state
     */
    toggle() {
        this.isEnabled = !this.isEnabled;
        this.settings.enabled = this.isEnabled;
        this.saveSettings();
        
        if (this.isEnabled) {
            this.startPolling();
            // console.log('[PositionDefenseBot] 🟢 Bot ENABLED - Starting 1-minute polling');
        } else {
            this.stopPolling();
            this.pendingVerification.clear();
            // console.log('[PositionDefenseBot] 🔴 Bot DISABLED - Stopped polling');
        }
        
        return this.isEnabled;
    }

    /**
     * Update settings
     */
    async updateSettings(newSettings) {
        this.settings = { ...this.settings, ...newSettings };
        this.isEnabled = this.settings.enabled;
        this.saveSettings();
        // console.log('[PositionDefenseBot] Settings updated:', this.settings);
    }

    /**
     * Get current status
     */
    getStatus() {
        return {
            enabled: this.isEnabled,
            initialized: this.isInitialized,
            settings: this.settings,
            pendingVerification: Array.from(this.pendingVerification.entries()).map(([key, data]) => ({
                key,
                asset: data.asset,
                size: data.size,
                side: data.side,
                missingTP: data.missingTP,
                missingSL: data.missingSL
            })),
            timestamp: Date.now()
        };
    }

    /**
     * Start polling interval (1 minute)
     */
    startPolling() {
        if (this.pollInterval) {
            // console.log('[PositionDefenseBot] Polling already running');
            return;
        }

        // console.log(`[PositionDefenseBot] 🔄 Starting polling (every ${this.pollIntervalMs / 1000}s)`);
        
        // Run immediately on start
        this.checkPositions();
        
        // Then poll every minute
        this.pollInterval = setInterval(() => {
            this.checkPositions();
        }, this.pollIntervalMs);
    }

    /**
     * Stop polling interval
     */
    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
            // console.log('[PositionDefenseBot] ⏸️ Polling stopped');
        }
    }

    /**
     * Main check cycle - called every minute
     */
    async checkPositions() {
        // console.log('[PositionDefenseBot] 🔍 CHECK CYCLE STARTED');
        
        if (!this.isEnabled || !this.isInitialized) {
            // console.log('[PositionDefenseBot] ⏸️ Skipping - enabled:', this.isEnabled, 'initialized:', this.isInitialized);
            return;
        }
        
        // LEADER ONLY: Prevent execution in follower tabs
        if (this.mode !== 'leader') {
            return;
        }

        // console.log('\n[PositionDefenseBot] ═══════════════════════════════════════════');
        // console.log('[PositionDefenseBot] 🔍 STARTING POSITION CHECK CYCLE');
        // console.log('[PositionDefenseBot] ═══════════════════════════════════════════');

        try {
            // Check if client is available
            if (!window.BrowserHyperliquidClient || !window.BrowserHyperliquidClient.isInitialized) {
                // console.log('[PositionDefenseBot] ⚠️ BrowserHyperliquidClient not initialized, skipping check');
                return;
            }

            // console.log(`[PositionDefenseBot] ⚙️ Current Settings: TP=${this.settings.tpPercent}%, SL=${this.settings.slPercent}%`);

            // Get all positions and orders
            // console.log('[PositionDefenseBot] 📡 Fetching positions and orders from Hyperliquid...');
            const [positions, openOrders] = await Promise.all([
                window.BrowserHyperliquidClient.getUserPositions(),
                window.BrowserHyperliquidClient.getUserOpenOrders()
            ]);

            // console.log(`[PositionDefenseBot] 📊 Retrieved ${positions.length} total positions, ${openOrders.length} total orders`);

            // Filter to active positions only (non-zero size)
            const activePositions = positions.filter(pos => {
                const size = Math.abs(parseFloat(pos.position.szi));
                return size > 0;
            });

            // console.log(`[PositionDefenseBot] 🎯 Found ${activePositions.length} ACTIVE positions (non-zero size)`);

            if (activePositions.length === 0) {
                // console.log('[PositionDefenseBot] ✅ No active positions to protect');
                this.pendingVerification.clear();
                return;
            }

            // Get market prices - try WebSocket first
            // console.log('[PositionDefenseBot] 💰 Fetching current market prices...');
            let allMids;
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                allMids = window.BrowserPriceService.getAllPrices();
                if (!allMids || Object.keys(allMids).length === 0) {
                    allMids = null;
                }
            }
            
            // Fallback to API
            if (!allMids) {
                allMids = await window.BrowserHyperliquidClient.infoClient.allMids();
            }

            // Check each active position
            for (const position of activePositions) {
                try {
                    await this.checkPosition(position, openOrders, allMids);
                } catch (error) {
                    // console.error(`[PositionDefenseBot] ❌ Error checking position ${position.position.coin}:`, error);
                }
            }

            // Clean up stale pending verifications (older than 5 minutes)
            this.cleanupStaleVerifications();

            // console.log('[PositionDefenseBot] ✅ Position check cycle complete\n');

        } catch (error) {
            // console.error('[PositionDefenseBot] ❌ Critical error in checkPositions:', error);
        }
    }

    /**
     * Check a single position for missing TP/SL
     */
    async checkPosition(position, openOrders, allMids) {
        const asset = position.position.coin;
        const positionSize = parseFloat(position.position.szi);
        const positionSizeAbs = Math.abs(positionSize);
        const isLong = positionSize > 0;
        const side = isLong ? 'LONG' : 'SHORT';
        const entryPrice = parseFloat(position.position.entryPx);

        // console.log(`\n[PositionDefenseBot] 📍 Checking ${asset}: ${side} ${positionSizeAbs} @ ${entryPrice}`);

        // Get current market price
        const currentPrice = parseFloat(allMids[asset]);
        if (!currentPrice || isNaN(currentPrice)) {
            // console.log(`[PositionDefenseBot]   ⚠️ Market price not available for ${asset}, skipping`);
            return;
        }
        // console.log(`[PositionDefenseBot]   💰 Current price: ${currentPrice}`);

        // Find TP/SL orders for this exact position (by asset AND size)
        const tpslResult = this.findTPSLForPosition(position, openOrders);
        
        // console.log(`[PositionDefenseBot]   📋 TP/SL Status: hasTP=${tpslResult.hasTP}, hasSL=${tpslResult.hasSL}`);
        // if (tpslResult.hasTP && tpslResult.tp) {
        //     console.log(`[PositionDefenseBot]   ✅ Found TP: ${tpslResult.tp.limitPx} (oid=${tpslResult.tp.oid})`);
        // }
        // if (tpslResult.hasSL && tpslResult.sl) {
        //     console.log(`[PositionDefenseBot]   ✅ Found SL: ${tpslResult.sl.limitPx} (oid=${tpslResult.sl.oid})`);
        // }

        // Check if missing TP or SL
        const missingTP = !tpslResult.hasTP;
        const missingSL = !tpslResult.hasSL;

        if (!missingTP && !missingSL) {
            // Has both TP and SL - clear any pending verification
            const key = this.getPositionKey(asset, positionSizeAbs);
            if (this.pendingVerification.has(key)) {
                // console.log(`[PositionDefenseBot]   ✅ Position now has both TP/SL - Clearing verification`);
                this.pendingVerification.delete(key);
            }
            return;
        }

        // Missing TP or SL - check verification status
        const key = this.getPositionKey(asset, positionSizeAbs);
        
        if (this.pendingVerification.has(key)) {
            // ✅ SECOND CONSECUTIVE CHECK - VERIFIED MISSING
            // console.log(`[PositionDefenseBot]   🛡️ VERIFIED (2nd check): ${asset} missing ${missingTP ? 'TP' : ''}${missingTP && missingSL ? ' and ' : ''}${missingSL ? 'SL' : ''}`);
            // console.log(`[PositionDefenseBot]   🔧 Adding missing TP/SL orders...`);
            
            const pending = this.pendingVerification.get(key);
            await this.addMissingTPSL(position, currentPrice, missingTP, missingSL);
            
            this.pendingVerification.delete(key);
        } else {
            // ⚠️ FIRST DETECTION - Mark for verification
            // console.log(`[PositionDefenseBot]   ⚠️ DETECTED (1st check): ${asset} missing ${missingTP ? 'TP' : ''}${missingTP && missingSL ? ' and ' : ''}${missingSL ? 'SL' : ''}`);
            // console.log(`[PositionDefenseBot]   ⏳ Will verify on next poll in 60 seconds...`);
            
            this.pendingVerification.set(key, {
                asset,
                size: positionSizeAbs,
                side,
                missingTP,
                missingSL,
                timestamp: Date.now()
            });
        }
    }

    /**
     * Find TP/SL orders for a specific position
     * Matches by asset AND exact size
     */
    findTPSLForPosition(position, openOrders) {
        const asset = position.position.coin;
        const positionSize = Math.abs(parseFloat(position.position.szi));
        const isLong = parseFloat(position.position.szi) > 0;
        const entryPrice = parseFloat(position.position.entryPx);

        // console.log(`[PositionDefenseBot] 🔍 Searching for TP/SL orders for ${asset}:`);
        // console.log(`[PositionDefenseBot]    Position size: ${positionSize}`);
        // console.log(`[PositionDefenseBot]    Entry price: ${entryPrice}`);
        // console.log(`[PositionDefenseBot]    Side: ${isLong ? 'LONG' : 'SHORT'}`);
        
        // Filter reduce-only orders for this asset with matching size
        const tpslOrders = openOrders.filter(order => {
            // Must match asset
            if (order.coin !== asset) return false;
            
            // Must be reduce-only
            if (!order.reduceOnly) return false;
            
            // Size must match position (with small tolerance for rounding)
            const orderSize = Math.abs(parseFloat(order.sz));
            const sizeDiff = Math.abs(orderSize - positionSize);
            const tolerance = positionSize * 0.01; // 1% tolerance
            if (sizeDiff > tolerance) {
                // console.log(`[PositionDefenseBot]    ❌ Rejected order ${order.oid}: size ${orderSize} differs by ${sizeDiff.toFixed(4)} (tolerance: ${tolerance.toFixed(4)})`);
                return false;
            }
            
            // console.log(`[PositionDefenseBot]    ✅ Accepted order ${order.oid}: price=${order.limitPx}, size=${orderSize}, trigger=${!!order.triggerPx}`);
            return true;
        });

        // console.log(`[PositionDefenseBot]   📦 Found ${tpslOrders.length} reduce-only orders matching ${asset} size ${positionSize}`);

        // Classify as TP or SL based on price relative to entry
        let tp = null;
        let sl = null;

        for (const order of tpslOrders) {
            const orderPrice = parseFloat(order.limitPx);
            
            // console.log(`[PositionDefenseBot]    🔵 Analyzing order ${order.oid}:`);
            // console.log(`[PositionDefenseBot]       Price: ${orderPrice}`);
            // console.log(`[PositionDefenseBot]       Entry: ${entryPrice}`);
            // console.log(`[PositionDefenseBot]       Trigger: ${order.triggerPx || 'NONE'}`);
            // console.log(`[PositionDefenseBot]       CLOID: ${order.cloid || 'NONE'}`);
            
            // Determine if this is profitable (TP) or loss (SL)
            // LONG: TP above entry, SL below entry
            // SHORT: TP below entry, SL above entry
            const isProfitable = isLong ?
                (orderPrice > entryPrice) :
                (orderPrice < entryPrice);
            
            // console.log(`[PositionDefenseBot]       isProfitable: ${isProfitable} (price ${orderPrice} ${isLong ? (orderPrice > entryPrice ? '>' : '<') : (orderPrice < entryPrice ? '<' : '>')} entry ${entryPrice})`);
            
            if (isProfitable) {
                if (!tp || (isLong ? orderPrice > parseFloat(tp.limitPx) : orderPrice < parseFloat(tp.limitPx))) {
                    tp = order;
                    // console.log(`[PositionDefenseBot]       ✅ Classified as TP`);
                }
            } else {
                if (!sl || (isLong ? orderPrice > parseFloat(sl.limitPx) : orderPrice < parseFloat(sl.limitPx))) {
                    sl = order;
                    // console.log(`[PositionDefenseBot]       ✅ Classified as SL`);
                }
            }
        }

        // console.log(`[PositionDefenseBot]   🎯 Final result: hasTP=${!!tp}, hasSL=${!!sl}`);
        
        return {
            hasTP: !!tp,
            hasSL: !!sl,
            tp,
            sl
        };
    }

    /**
     * Add missing TP/SL orders for a position
     */
    async addMissingTPSL(position, currentPrice, needsTP, needsSL) {
        const asset = position.position.coin;
        const positionSize = Math.abs(parseFloat(position.position.szi));
        const isLong = parseFloat(position.position.szi) > 0;
        const side = isLong ? 'LONG' : 'SHORT';

        // console.log(`[PositionDefenseBot]   🎯 Calculating TP/SL prices for ${asset} ${side} ${positionSize}`);
        // console.log(`[PositionDefenseBot]   💰 Current price: ${currentPrice}, Settings: TP=${this.settings.tpPercent}%, SL=${this.settings.slPercent}%`);

        // Validate inputs
        if (!currentPrice || isNaN(currentPrice) || currentPrice <= 0) {
            // console.error(`[PositionDefenseBot]   ❌ Invalid current price: ${currentPrice}`);
            throw new Error(`Invalid current price for ${asset}: ${currentPrice}`);
        }
        
        if (positionSize === 0 || isNaN(positionSize)) {
            // console.error(`[PositionDefenseBot]   ❌ Invalid position size: ${positionSize}`);
            throw new Error(`Invalid position size for ${asset}: ${positionSize}`);
        }

        // Calculate TP and SL prices based on current price and %
        let tpPrice = null;
        let slPrice = null;

        if (needsTP) {
            // TP is in profit direction
            tpPrice = isLong ?
                currentPrice * (1 + this.settings.tpPercent / 100) :   // Long TP higher
                currentPrice * (1 - this.settings.tpPercent / 100);     // Short TP lower
            
            // Validate TP price
            if (isNaN(tpPrice) || tpPrice <= 0 || !isFinite(tpPrice)) {
                // console.error(`[PositionDefenseBot]   ❌ Invalid TP price calculated: ${tpPrice}`);
                throw new Error(`Invalid TP price: ${tpPrice}`);
            }
            
            // console.log(`[PositionDefenseBot]   📈 Calculated TP: ${tpPrice} (${this.settings.tpPercent}% ${isLong ? 'above' : 'below'} current)`);
        }

        if (needsSL) {
            // SL is in loss direction
            slPrice = isLong ?
                currentPrice * (1 - this.settings.slPercent / 100) :   // Long SL lower
                currentPrice * (1 + this.settings.slPercent / 100);     // Short SL higher
            
            // Validate SL price
            if (isNaN(slPrice) || slPrice <= 0 || !isFinite(slPrice)) {
                // console.error(`[PositionDefenseBot]   ❌ Invalid SL price calculated: ${slPrice}`);
                throw new Error(`Invalid SL price: ${slPrice}`);
            }
            
            // console.log(`[PositionDefenseBot]   📉 Calculated SL: ${slPrice} (${this.settings.slPercent}% ${isLong ? 'below' : 'above'} current)`);
        }

        try {
            // Place TP/SL orders using executeTradingOrder
            // console.log(`[PositionDefenseBot]   🚀 Placing ${needsTP ? 'TP' : ''}${needsTP && needsSL ? ' and ' : ''}${needsSL ? 'SL' : ''} order(s)...`);
            
            // CRITICAL: orderSide should be the ENTRY order side (not TP/SL side)
            // buildTPOrder/buildSLOrder will flip it to opposite direction
            // LONG position → entry was BUY → orderSide="buy" → TP/SL will be SELL ✓
            // SHORT position → entry was SELL → orderSide="sell" → TP/SL will be BUY ✓
            const result = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset: asset,
                orderSide: isLong ? "buy" : "sell",  // Entry order side (gets flipped for TP/SL)
                size: positionSize,
                orderType: "limit",
                price: null,                          // No entry price (TP/SL only mode)
                tpslEnabled: true,
                tpPrice: needsTP ? tpPrice : null,
                slPrice: needsSL ? slPrice : null,
                timeInForce: "GTC",
                reduceOnly: true
            });

            if (result.success) {
                // console.log(`[PositionDefenseBot]   ✅ Successfully placed ${needsTP ? 'TP' : ''}${needsTP && needsSL ? ' and ' : ''}${needsSL ? 'SL' : ''} for ${asset}`);
                // console.log(`[PositionDefenseBot]   📝 Result:`, result);
            } else {
                // console.error(`[PositionDefenseBot]   ❌ Failed to place orders:`, result.error);
            }

            return result;

        } catch (error) {
            // console.error(`[PositionDefenseBot]   ❌ Error placing TP/SL:`, error);
            throw error;
        }
    }

    /**
     * Generate unique key for position tracking (asset + size)
     */
    getPositionKey(asset, size) {
        return `${asset}_${size.toFixed(6)}`;
    }

    /**
     * Clean up stale pending verifications (older than 5 minutes)
     */
    cleanupStaleVerifications() {
        const now = Date.now();
        const staleThreshold = 5 * 60 * 1000; // 5 minutes

        let cleanedCount = 0;
        for (const [key, data] of this.pendingVerification.entries()) {
            if (now - data.timestamp > staleThreshold) {
                this.pendingVerification.delete(key);
                cleanedCount++;
            }
        }

        // if (cleanedCount > 0) {
        //     console.log(`[PositionDefenseBot] 🧹 Cleaned up ${cleanedCount} stale verification(s)`);
        // }
    }

    /**
     * Set mode (for TabCoordinator)
     * @param {string} newMode - 'leader' or 'follower'
     */
    async setMode(newMode) {
        console.log(`[PositionDefenseBot] Switching mode: ${this.mode} -> ${newMode}`);
        this.mode = newMode;
    }
}

// Create and expose singleton instance
if (typeof window !== 'undefined') {
    window.BrowserPositionDefenseBot = new BrowserPositionDefenseBot();
    // console.log('✅ Position Defense Bot singleton initialized on window');
}
